import React, { useState } from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'gatsby-plugin-intl'
import moment from 'moment'
import { breakpoint } from 'src/theme/grid'
import useClickOutside from 'src/hooks/useClickOutside'
import DatePicker from 'src/components/atoms/DatePicker'
import TextInputMasked from 'src/components/atoms/TextInputMasked'
import {
  FilterBox,
  FilterDropdown,
  SelectOptionsWrapper,
  SelectRow
} from 'src/components/filters/components/atoms'
import { useOfferAgeFilter } from './OfferAgeFilterContext'
import {
  completeOfferDateRange,
  createOfferDatePreset,
  getOfferDateBounds,
  getOfferDateRangeFields,
  getOfferDateRangeFromFields
} from './offerAgeFilter'

const EMPTY_RANGE = {
  fields: getOfferDateRangeFields('all'),
  range: 'all'
}

export const OfferAgeFilter = ({ id }) => {
  const intl = useIntl()
  const { range, setRange } = useOfferAgeFilter()
  const [opened, setOpened] = useState(false)
  const [draftRange, setDraftRange] = useState(EMPTY_RANGE)
  const [invalid, setInvalid] = useState(false)
  const componentRef = useClickOutside(() => setOpened(false))
  const label = intl.formatMessage({ id: 'offersFilters.createdAtWindow' })
  const options = [
    {
      value: '15min',
      label: intl.formatMessage({ id: 'offersFilters.createdAtLast15Minutes' }),
      minutes: 15
    },
    {
      value: '1h',
      label: intl.formatMessage({ id: 'offersFilters.createdAtLast1Hour' }),
      minutes: 60
    },
    {
      value: '24h',
      label: intl.formatMessage({ id: 'offersFilters.createdAtLast24Hours' }),
      minutes: 1440
    }
  ]
  const bounds = getOfferDateBounds(range)
  const caption = bounds
    ? intl.formatMessage(
        { id: 'offersFilters.createdAtRangeSummary' },
        {
          from: moment(bounds.from).format('DD.MM.YYYY HH:mm'),
          to: moment(bounds.to).format('DD.MM.YYYY HH:mm')
        }
      )
    : label

  const toggleDropdown = () => {
    if (!opened) {
      setDraftRange({ fields: getOfferDateRangeFields(range), range })
      setInvalid(false)
    }
    setOpened(!opened)
  }
  const clear = event => {
    if (event) event.stopPropagation()
    setRange('all')
    setDraftRange(EMPTY_RANGE)
    setInvalid(false)
  }
  const selectOption = option => {
    const presetRange = {
      ...createOfferDatePreset(option.minutes),
      preset: option.value
    }
    setDraftRange({
      fields: getOfferDateRangeFields(presetRange),
      range: presetRange
    })
    setInvalid(false)
  }
  const updateRange = (name, value) => {
    const fieldValue =
      name.endsWith('Time') && !/\d/.test(value || '') ? '' : value || ''
    setDraftRange(previous => {
      const fields = { ...previous.fields, [name]: fieldValue }
      return { fields, range: getOfferDateRangeFromFields(fields) }
    })
    setInvalid(false)
  }
  const confirm = () => {
    const completedDraft = draftRange.range
      ? draftRange
      : completeOfferDateRange(draftRange.fields)
    setDraftRange(completedDraft)
    if (!completedDraft.range) {
      setInvalid(true)
      return
    }
    setRange(completedDraft.range)
    setOpened(false)
  }

  return (
    <FilterWrapper id={id} ref={componentRef}>
      <FilterTrigger
        opened={opened}
        hasValue={range !== 'all'}
        onClick={toggleDropdown}
        onClear={clear}
      >
        <span title={caption}>{caption}</span>
      </FilterTrigger>
      {opened && (
        <AgeDropdown
          stickyActions
          filterName={label}
          onClose={() => setOpened(false)}
          onConfirm={confirm}
          onClear={clear}
          clearButtonText={intl.formatMessage({ id: 'offersFilters.clear' })}
          confirmButtonText={intl.formatMessage({
            id: 'offersFilters.confirm'
          })}
        >
          <SelectOptionsWrapper>
            {options.map(option => (
              <SelectRow
                key={option.value}
                type='radio'
                selected={Boolean(
                  draftRange.range && draftRange.range.preset === option.value
                )}
                name={option.label}
                onClick={() => selectOption(option)}
              />
            ))}
            <RangeForm
              onSubmit={event => {
                event.preventDefault()
                confirm()
              }}
            >
              <RangeFields>
                {['from', 'to'].map(name => {
                  const fieldLabel = intl.formatMessage({
                    id:
                      name === 'from'
                        ? 'offersFilters.createdAtFrom'
                        : 'offersFilters.createdAtTo'
                  })
                  return (
                    <Field key={name}>
                      <legend>{fieldLabel}</legend>
                      <DateTimeFields>
                        <DatePicker
                          id={`${id}-${name}-date`}
                          name={`${name}Date`}
                          label={intl.formatMessage({
                            id: 'offersFilters.createdAtDate'
                          })}
                          placeholder='dd.mm.yyyy'
                          value={draftRange.fields[`${name}Date`] || null}
                          onChange={updateRange}
                          popoverContainer={() => componentRef.current}
                          pristine
                        />
                        <TextInputMasked
                          id={`${id}-${name}-time`}
                          name={`${name}Time`}
                          label={intl.formatMessage({
                            id: 'offersFilters.createdAtTime'
                          })}
                          mask='99:99'
                          type='tel'
                          placeholder='HH:mm'
                          calendarTime
                          value={draftRange.fields[`${name}Time`]}
                          onChange={updateRange}
                        />
                      </DateTimeFields>
                    </Field>
                  )
                })}
              </RangeFields>
              {invalid && (
                <RangeError role='alert'>
                  {intl.formatMessage({
                    id: 'offersFilters.createdAtInvalidRange'
                  })}
                </RangeError>
              )}
              <HiddenSubmit type='submit' tabIndex={-1} aria-hidden='true' />
            </RangeForm>
          </SelectOptionsWrapper>
        </AgeDropdown>
      )}
    </FilterWrapper>
  )
}

const FilterWrapper = styled.div`
  display: flex;
  position: relative;
`
const FilterTrigger = styled(FilterBox)`
  width: 100%;
  ${breakpoint.m`width: 180px;`}
`
const AgeDropdown = styled(FilterDropdown)`
  ${breakpoint.m`
    width: 360px;
    max-width: calc(100vw - 32px);
    height: 480px;
    max-height: calc(100vh - 80px);
  `}
`
const RangeForm = styled.form`
  padding: 0.6rem 0.8rem;
`
const RangeFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 0.6rem;
`
const Field = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 0.85rem;
  legend {
    padding: 0;
    margin-bottom: 0.4rem;
    font-weight: 600;
  }
`
const DateTimeFields = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 0.6rem;
  > div > span:last-child:empty {
    display: none;
  }
`
const RangeError = styled.p`
  margin: 0.6rem 0 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.error.hex()};
`
const HiddenSubmit = styled.button`
  display: none;
`

export const OfferAgeFilterCount = ({ count }) => (
  <>
    <span>{`${count} `}</span>
    <FormattedMessage id='offers.offersFound' values={{ count }} />
  </>
)
